import { type ReactNode, createContext, useContext, useState, useCallback } from 'react';
import { Check, AlertCircle, X } from 'lucide-react';

type ToastType = 'success' | 'warning' | 'error';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((message: string, type: ToastType = 'success') => {
    const id = `${Date.now()}-${Math.random()}`;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const iconFor = (type: ToastType) => {
    switch (type) {
      case 'success':
        return <Check size={16} className="text-[#00D4AA]" />;
      case 'warning':
        return <AlertCircle size={16} className="text-[#F6AD55]" />;
      case 'error':
        return <X size={16} className="text-[#E53E3E]" />;
      default:
        return null;
    }
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed right-4 top-4 z-50 flex flex-col gap-3">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`flex max-w-sm items-center gap-3 rounded-lg border px-5 py-3 text-sm font-medium shadow-lg ${
              toast.type === 'error'
                ? 'border-[#E53E3E] bg-[#E53E3E]/12 text-[#E53E3E]'
                : toast.type === 'warning'
                  ? 'border-[#F6AD55] bg-[#F6AD55]/12 text-[#F6AD55]'
                  : 'border-[#00D4AA] bg-[#00D4AA]/12 text-[#00D4AA]'
            }`}
          >
            {iconFor(toast.type)}
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

export default ToastProvider;
